/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prettier/prettier */
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable import/order */
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { buildValidationSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, TextField, Typography } from '@mui/material';
import { Form } from '@bvi/common-components';
import { LoadingButton } from '@mui/lab';
import { useCreateSubsidiaryMutation } from '@bvi/admin-panel/entities/subsidiary/api-slice';
import { useCreateOrganizationMutation } from '@bvi/admin-panel/entities/organization/api-slice';
import { ManageBaseDataKeys } from '@bvi/api-interfaces/entity/manage-base';
import { useCreatePerpetratorMutation } from '@bvi/admin-panel/entities/perpetrator/api-slice';
import { useCreateDefenseMutation } from '@bvi/admin-panel/entities/defense/api-slice';
import { useCreateExceptionMutation } from '@bvi/admin-panel/entities/exception/api-slice';
import { useCreateOrganizationTypeMutation } from '@bvi/admin-panel/entities/organization-type/api-slice';
import { useCreateJobPositionMutation } from '@bvi/admin-panel/entities/job-position/api-slice';
import { useCreateOrderMutation } from '@bvi/admin-panel/entities/order/api-slice';
import { useCreateDamageMutation } from '@bvi/admin-panel/entities/damage/api-slice';
import { useCreateCountryMutation } from '@bvi/admin-panel/entities/country/api-slice';

interface IAddManageFormProps {
  type: string;
  onSuccess: () => void;
}

interface IAddManageFormValues {
  name: string;
}

export const AddManageForm: FC<IAddManageFormProps> = ({ type, onSuccess }) => {
  const { t } = useTranslation();
  const validationSchema = buildValidationSchema(t);
  const [createSubsidiary, { isLoading: isCreatingSubsidiary }] = useCreateSubsidiaryMutation();
  const [createOrganization, { isLoading: isCreatingOrganization }] = useCreateOrganizationMutation();
  const [createPerpetrator, { isLoading: isCreatingPerpetrator }] = useCreatePerpetratorMutation();
  const [createDefense, { isLoading: isCreatingDefense }] = useCreateDefenseMutation();
  const [createException, { isLoading: isCreatingException }] = useCreateExceptionMutation();
  const [createOrganizationType, { isLoading: isCreatingOrganizationType }] = useCreateOrganizationTypeMutation();
  const [createJobPosition, { isLoading: isCreatingJobPosition }] = useCreateJobPositionMutation();
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateOrderMutation();
  const [createDamage, { isLoading: isCreatingDamage }] = useCreateDamageMutation();
  const [createCountry, { isLoading: isCreatingCountry }] = useCreateCountryMutation();

  const methods = useForm<IAddManageFormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: '',
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const onSubmit = async (data: IAddManageFormValues) => {
    switch (type) {
      case ManageBaseDataKeys.SUBSIDIARIES: {
        await createSubsidiary(data);
        break;
      }
      case ManageBaseDataKeys.ORGANIZATIONS: {
        await createOrganization(data);
        break;
      }
      case ManageBaseDataKeys.PERPETRATORS: {
        await createPerpetrator(data);
        break;
      }
      case ManageBaseDataKeys.DEFENSES: {
        await createDefense(data);
        break;
      }
      case ManageBaseDataKeys.EXCEPTIONS: {
        await createException(data);
        break;
      }
      case ManageBaseDataKeys.ORGANIZATION_TYPES: {
        await createOrganizationType(data);
        break;
      }
      case ManageBaseDataKeys.JOB_POSITIONS: {
        await createJobPosition(data);
        break;
      }
      case ManageBaseDataKeys.ORDERS: {
        await createOrder(data);
        break;
      }
      case ManageBaseDataKeys.DAMAGES: {
        await createDamage(data);
        break;
      }
      case ManageBaseDataKeys.COUNTRIES: {
        await createCountry(data);
        break;
      }
      default: {
        break;
      }
    }
    onSuccess?.();
  };

  return (
    <FormProvider {...methods}>
        <Typography variant="h1" mb={3}>
          {t('manageList.form.addTitle')}
        </Typography>
        <Form
          noValidate
          tabIndex={1}
          onSubmit={handleSubmit(onSubmit)}
          sx={{ width: '400px' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('name')}
                variant="outlined"
                fullWidth
                label={t('manageList.form.fields.name.label')}
                placeholder={t('manageList.form.fields.name.placeholder')}
                error={Boolean(errors.name)}
                helperText={<>{errors.name?.message}</>}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} marginTop={1}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={
                isCreatingSubsidiary ||
                isCreatingOrganization ||
                isCreatingOrganizationType ||
                isCreatingPerpetrator ||
                isCreatingDefense ||
                isCreatingException ||
                isCreatingJobPosition ||
                isCreatingOrder ||
                isCreatingDamage ||
                isCreatingCountry
              }
              fullWidth
              autoFocus
            >
              {t('companies.edit.form.submit')}
            </LoadingButton>
          </Grid>
        </Form>
      </FormProvider>
  );
};
